import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { ZeytalTokens } from "@zeytal/types";

import { translateCjs } from "../core/translators/cjs.ts";
import { translateCss } from "../core/translators/css.ts";
import { translateEsm } from "../core/translators/esm.ts";
import { translateJson } from "../core/translators/json.ts";
import { translateLess } from "../core/translators/less.ts";
import { translateSass } from "../core/translators/sass.ts";
import { translateScss } from "../core/translators/scss.ts";
import { translateTs } from "../core/translators/ts.ts";
import { translateTwV4 } from "../core/translators/tw4.ts";

const authorizedTranslations: ZeytalCLIPureConfig["styles"]["formats"]["targets"] =
  ["tw", "ts", "esm", "cjs", "json", "css", "scss", "sass", "less"];

const translationsFilesBasenames = {
  twV4: "styles-tw",
  twV3: "styles-tw",
  ts: "styles",
  esm: "styles",
  cjs: "styles",
  json: "styles",
  css: "styles",
  scss: "styles",
  sass: "styles",
  less: "styles",
};

const translationsFilesExtensions = {
  twV4: "css",
  twV3: "js",
  ts: "ts",
  esm: "mjs",
  cjs: "cjs",
  json: "json",
  css: "css",
  scss: "scss",
  sass: "sass",
  less: "less",
};

const authorizedTranslationsFilter = (
  target: ZeytalCLIPureConfig["styles"]["formats"]["targets"][number],
) => authorizedTranslations.includes(target);

const getTranslationFilename = (
  target: ZeytalCLIPureConfig["styles"]["formats"]["targets"][number],
) => {
  let filename;
  switch (target) {
    case "tw":
      filename = `${translationsFilesBasenames.twV4}.${translationsFilesExtensions.twV4}`;
      break;
    case "ts":
      filename = `${translationsFilesBasenames.ts}.${translationsFilesExtensions.ts}`;
      break;
    case "esm":
      filename = `${translationsFilesBasenames.esm}.${translationsFilesExtensions.esm}`;
      break;
    case "cjs":
      filename = `${translationsFilesBasenames.cjs}.${translationsFilesExtensions.cjs}`;
      break;
    case "json":
      filename = `${translationsFilesBasenames.json}.${translationsFilesExtensions.json}`;
      break;
    case "css":
      filename = `${translationsFilesBasenames.css}.${translationsFilesExtensions.css}`;
      break;
    case "scss":
      filename = `${translationsFilesBasenames.scss}.${translationsFilesExtensions.scss}`;
      break;
    case "sass":
      filename = `${translationsFilesBasenames.sass}.${translationsFilesExtensions.sass}`;
      break;
    case "less":
      filename = `${translationsFilesBasenames.less}.${translationsFilesExtensions.less}`;
      break;
    default:
      filename = "";
      break;
  }

  return filename;
};

const translateStylesInTargetLanguage = (
  target: ZeytalCLIPureConfig["styles"]["formats"]["targets"][number],
  userConfig: ZeytalCLIPureConfig,
  styles: ZeytalTokens.TransformedTokensArray,
) => {
  switch (target) {
    case "tw":
      return translateTwV4(styles, userConfig);
    case "ts":
      return translateTs(styles);
    case "esm":
      return translateEsm(styles);
    case "cjs":
      return translateCjs(styles);
    case "json":
      return translateJson(styles);
    case "css":
      return translateCss(styles);
    case "scss":
      return translateScss(styles);
    case "sass":
      return translateSass(styles);
    case "less":
      return translateLess(styles);
    default:
      return "";
  }
};

export const translateStyles = (
  styles: ZeytalTokens.TransformedTokensArray,
  userConfig: ZeytalCLIPureConfig,
) => {
  const { targets } = userConfig.styles.formats;

  const authorizedTargets = [...new Set(targets)].filter(
    authorizedTranslationsFilter,
  ) as ZeytalCLIPureConfig["styles"]["formats"]["targets"];

  // Make an object initialized with targets as key and an empty object as value for the soon to be translated styles values.
  const transformedValuesByTarget = Object.assign(
    {},
    ...authorizedTargets.map((target) => ({
      [getTranslationFilename(target)]: {},
    })),
  ) as Record<string, string>;

  authorizedTargets.forEach((target) => {
    transformedValuesByTarget[getTranslationFilename(target)] =
      translateStylesInTargetLanguage(target, userConfig, styles);
  });

  return transformedValuesByTarget;
};
